/**
 * @fileoverview CorporateIdentity component for managing company identity information.
 * This component handles the display and editing of core company data including
 * business name, legal name, logo, operation environment, company size, and
 * economic activity classifications.
 */

import styles from "./CorporateIdentity.module.css";
import CorporateIdentityFields from "./CorporateIdentityFields";
import CorporateIdentityLogoUpload from "./CorporateIdentityLogoUpload";
import CorporateIdentitySectorFields from "./CorporateIdentitySectorFields";
import { useCorporateIdentitySection } from "./useCorporateIdentitySection";

/**
 * CorporateIdentity component for displaying and editing company identity information.
 * Manages company logo, business name, legal name, operation environment, company size,
 * and economic classification fields with profile-based visibility controls.
 *
 * @param {Object} props - Component props
 * @param {Object} props.company - Company data object containing corporate identity fields
 * @param {Function} props.onFieldChange - Callback function triggered when a field value changes
 * @param {Function} props.onValidationChange - Callback function triggered when validation state changes
 * @param {number} [props.selectedProfile=0] - Currently selected company profile index for multi-profile support
 * @returns {JSX.Element} The rendered CorporateIdentity form section
 */
const CorporateIdentity = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const section = useCorporateIdentitySection({
    company,
    onFieldChange,
    onValidationChange,
    selectedProfile,
  });

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {section.t("components.corporateIdentity.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <CorporateIdentityFields
        company={company}
        selectedProfile={selectedProfile}
        section={section}
      />
      <CorporateIdentitySectorFields
        company={company}
        selectedProfile={selectedProfile}
        section={section}
      />
      <CorporateIdentityLogoUpload
        selectedProfile={selectedProfile}
        section={section}
      />
    </div>
  );
};

export default CorporateIdentity;
